import { useEffect, useState } from "react";

import { API_URL } from "../config/api";

export type AdminRole = "club" | "admin";

type Session = {
  token: string;
  role: AdminRole;
  expiresAt: number;
};

const STORAGE_KEY = "adminSession";
export const LOGOUT_EVENT = "admin-logout";
export const LOGIN_EVENT = "admin-login";

const ROLE_RANK: Record<AdminRole, number> = { club: 1, admin: 2 };

function readSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const session = raw ? (JSON.parse(raw) as Session) : null;

    return session && session.expiresAt > Date.now() ? session : null;
  } catch {
    return null;
  }
}

export function getRole(): AdminRole | null {
  return readSession()?.role ?? null;
}

// Rôle de la session en cours, mis à jour à la connexion/déconnexion.
export function useAdminRole(): AdminRole | null {
  const [role, setRole] = useState(getRole);

  useEffect(() => {
    const refresh = () => setRole(getRole());

    window.addEventListener(LOGIN_EVENT, refresh);
    window.addEventListener(LOGOUT_EVENT, refresh);
    return () => {
      window.removeEventListener(LOGIN_EVENT, refresh);
      window.removeEventListener(LOGOUT_EVENT, refresh);
    };
  }, []);

  return role;
}

export function hasRole(minRole: AdminRole): boolean {
  const session = readSession();

  return !!session && ROLE_RANK[session.role] >= ROLE_RANK[minRole];
}

export async function adminLogin(password: string): Promise<AdminRole> {
  const response = await fetch(`${API_URL}/admin/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });

  const body = await response.json();

  if (!response.ok) {
    throw new Error(body.error ?? "Connexion impossible");
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(body));
  } catch {
    // stockage indisponible : la session ne survivra pas au rechargement
  }

  window.dispatchEvent(new Event(LOGIN_EVENT));

  return body.role;
}

export function adminLogout(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignoré
  }

  window.dispatchEvent(new Event(LOGOUT_EVENT));
}

// fetch avec le jeton admin ; une session expirée ou refusée par le
// serveur renvoie vers l'écran de connexion.
export async function adminFetch(
  url: string,
  init: RequestInit = {}
): Promise<Response> {
  const session = readSession();

  const response = await fetch(url, {
    ...init,
    headers: {
      ...init.headers,
      Authorization: `Bearer ${session?.token ?? ""}`,
    },
  });

  if (response.status === 401) {
    adminLogout();
  }

  return response;
}
